import { useState, useRef, useCallback } from 'react';

const MAX_RECORDING_DURATION = 30; // Maximum 30 seconds

export const useRecorder = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [audioUrl, setAudioUrl] = useState(null);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 16000,
        },
      });

      // Determine optimal mimeType and bitrate
      // DeepInfra Whisper supports: wav, mp3, mp4, m4a, ogg (NOT webm!)
      // 降低比特率以避免 500 错误（239KB 文件太大）
      let mimeType = 'audio/mp4'; // 默认使用 mp4，兼容性好
      let audioBitsPerSecond = 16000; // 16 kbps - 降低比特率以减小文件大小

      // Check supported formats in order of preference for DeepInfra
      const supportedFormats = [
        'audio/mp4',      // Safari, Chrome (首选)
        'audio/wav',      // 所有浏览器都支持，但文件大
        'audio/ogg',      // Firefox, Chrome
        'audio/webm',     // Chrome, Firefox (但DeepInfra不支持)
      ];

      for (const format of supportedFormats) {
        if (MediaRecorder.isTypeSupported(format)) {
          mimeType = format;
          // 根据格式调整比特率
          if (format === 'audio/wav') {
            audioBitsPerSecond = 64000; // wav需要更高比特率，但降低到64kbps
          } else if (format === 'audio/webm') {
            // 如果只支持webm，就使用它，但可能会失败
            audioBitsPerSecond = 16000;
          } else {
            audioBitsPerSecond = 16000; // mp4, ogg - 降低到16kbps
          }
          break;
        }
      }

      // 移除开始录音的日志，减少控制台输出
      // console.log(`🎙️ Starting recording: ${mimeType} @ ${audioBitsPerSecond} bps`);

      const mediaRecorder = new MediaRecorder(stream, {
        mimeType,
        audioBitsPerSecond, // Limit bitrate to reduce file size
      });

      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
          // 移除频繁的日志输出，避免控制台滚动
          // console.log(`📦 Audio chunk received: ${event.data.size} bytes`);
        }
      };

      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, {
          type: mediaRecorder.mimeType,
        });
        const url = URL.createObjectURL(audioBlob);
        setAudioUrl(url);

        // 只在文件大小异常时输出日志（调试用）
        // const fileSizeKB = (audioBlob.size / 1024).toFixed(1);
        // console.log(`✅ Recording stopped. Size: ${fileSizeKB} KB`);

        // Update recording state AFTER stopping
        setIsRecording(false);

        // Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
        }

        // Stop all tracks
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start(100); // Collect data every 100ms for more efficient processing
      setIsRecording(true);
      setDuration(0);

      // Start timer with auto-stop at MAX_RECORDING_DURATION
      timerRef.current = setInterval(() => {
        setDuration((prev) => {
          const newDuration = prev + 1;
          if (newDuration >= MAX_RECORDING_DURATION) {
            // Auto-stop when reaching max duration
            console.log(`⏱️ Maximum recording duration (${MAX_RECORDING_DURATION}s) reached, auto-stopping...`);
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              mediaRecorderRef.current.stop();
              // Don't setIsRecording here - let onstop handle it
            }
          }
          return newDuration;
        });
      }, 1000);
    } catch (err) {
      console.error('Error starting recording:', err);
      throw err;
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      // Don't set isRecording here - let onstop callback handle it
      mediaRecorderRef.current.stop();

      // Clear timer immediately
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    }
  };

  const resetRecording = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioUrl(null);
    setDuration(0);
    setIsRecording(false);
  };

  const getAudioFile = useCallback(() => {
    if (!audioUrl) return null;

    return fetch(audioUrl)
      .then((res) => res.blob())
      .then((blob) => {
        const extension = blob.type.includes('webm') ? 'webm' : 'm4a';
        return new File([blob], `recording.${extension}`, { type: blob.type });
      });
  }, [audioUrl]);

  return {
    isRecording,
    audioUrl,
    duration,
    startRecording,
    stopRecording,
    resetRecording,
    getAudioFile,
  };
};
